<template>
  <v-container class="pa-0">
    <v-row>
      <v-col cols="12">
        <v-card outlined class="modern-card overflow-hidden shadow-sm">
          <v-data-table
            :headers="headers"
            :items="items"
            :search="search"
            sort-by="dte__date_emited"
            :sort-desc="true"
            :items-per-page="5"
            :loading="loading"
            :options.sync="options"
            :server-items-length="totalItems"
            class="modern-table"
            :footer-props="{
              showFirstLastPage: true,
              itemsPerPageOptions: [5, 10, 20, 50],
            }"
          >
            <!-- Integrated Top Bar -->
            <template v-slot:top>
              <v-sheet color="bg-lightgrey" class="px-6 py-4 d-flex align-center border-bottom bg-lightgrey">
                <div class="d-flex align-center">
                  <v-avatar size="32" color="primary lighten-5" class="mr-3">
                    <v-icon color="primary" small>fas fa-list-ul</v-icon>
                  </v-avatar>
                  <span class="caption font-weight-black uppercase tracking-widest grey--text">
                    Relación de Documentos ({{ totalItems }})
                  </span>
                </div>
                <v-spacer></v-spacer>
                <div class="d-flex align-center" style="gap: 16px;">
                  <export-product-invoice-detail 
                    :product="product"
                    :filter="filter" 
                    :options="options" 
                    :disabled="loading"
                  />
                </div>
              </v-sheet>
            </template>

            <!-- Document ID with NC flag -->
            <template v-slot:item.dte__document_id="{ item }">
              <div class="d-flex align-center">
                <span class="body-2 font-weight-black primary--text mr-2">{{ item.dte__document_id }}</span>
                <v-chip v-if="item.tipo == 60" x-small color="error" class="font-weight-black" label>
                  NC
                </v-chip>
              </div>
            </template>

            <!-- Emission Date -->
            <template v-slot:item.dte__date_emited="{ item }">
              <span class="caption grey--text font-weight-bold">
                {{ item.dte__date_emited | moment('DD/MM/YYYY') }}
              </span>
            </template>

            <!-- Receipient / Customer -->
            <template v-slot:item.dte__rznSocRecep="{ item }">
              <div class="caption font-weight-medium grey--text text--darken-2 lh-1-2 py-2">
                {{ item.dte__rznSocRecep }}
              </div>
            </template>

            <!-- OT Reference -->
            <template v-slot:item.dte__work_order__id="{ item }">
              <v-chip v-if="item.dte__work_order__id" x-small label color="primary" class="font-weight-black">
                OT #{{ item.dte__work_order__id }}
              </v-chip>
              <span v-else class="caption grey--text opacity-40">-</span>
            </template>

            <!-- CN Reference -->
            <template v-slot:item.dte__work_order__sales_note__id="{ item }">
              <span v-if="item.dte__work_order__sales_note__id" class="caption font-weight-black secondary--text">
                CN #{{ item.dte__work_order__sales_note__id }}
              </span>
              <span v-else class="caption grey--text opacity-40">-</span>
            </template>

            <!-- Price & Quantity -->
            <template v-slot:item.avg_price="{ item }">
              <span class="body-2 font-weight-medium">{{ item.avg_price | moneda }}</span>
            </template>

            <template v-slot:item.qty="{ item }">
              <span class="body-2 font-weight-black">{{ item.qty | number('0,0') }}</span>
              <span class="caption grey--text ml-1">un</span>
            </template>

            <!-- Total -->
            <template v-slot:item.total="{ item }">
              <div class="body-2 font-weight-black secondary--text tracking-tighter">
                {{ item.total | moneda }}
              </div>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { fetchInvoicedDetailByProducts } from '@/api/reports'
import ExportProductInvoiceDetail from '@/components/reports/ExportProductInvoiceDetail'
import notifications from '@/utils/notifications'

export default {
  name: 'ProductInvoiceDetail',
  props: ['product', 'filter'],
  components: { ExportProductInvoiceDetail },
  data() {
    return {
      options: {},
      items: [],
      totalItems: 0,
      search: '',
      loading: false,
      headers: [
        { text: 'Documento', value: 'dte__document_id', class: 'table-header', width: '130px' },
        { text: 'Emitido', value: 'dte__date_emited', class: 'table-header', width: '110px' },
        { text: 'Razón Social / Cliente', value: 'dte__rznSocRecep', class: 'table-header' },
        { text: 'OT', value: 'dte__work_order__id', class: 'table-header', align: 'start', width: '100px' },
        { text: 'CN', value: 'dte__work_order__sales_note__id', class: 'table-header', align: 'start', width: '100px' },
        { text: 'P. Unit.', value: 'avg_price', class: 'table-header', align: 'end', width: '110px' },
        { text: 'Cant.', value: 'qty', class: 'table-header', align: 'end', width: '90px' },
        { text: 'Total', value: 'total', class: 'table-header', align: 'end', width: '130px' }
      ]
    }
  },
  methods: {
    async fetch() {
      if (!this.product || !this.product.product) return
      
      this.loading = true
      const created_after = this.filter.created_after || ''
      const created_before = this.filter.created_before || ''
      const seller = this.filter.seller || ''
      
      const { sortBy, sortDesc, page, itemsPerPage } = this.options
      let sort = (sortDesc && sortDesc[0] ? '-' + sortBy[0] : (sortBy ? sortBy[0] : 'dte__date_emited'))
      if (!sort) sort = 'dte__date_emited'
      
      const query = `page=${page}&page_size=${itemsPerPage}&search=${this.search}&ordering=${sort}&product=${this.product.product}&emited_after=${created_after}&emited_before=${created_before}&dte__customer__seller__id=${seller}`
      
      try {
        const res = await fetchInvoicedDetailByProducts(query)
        this.totalItems = res.count
        this.items = res.results
      } catch (err) {
        notifications.showError(err)
      } finally {
        this.loading = false
      }
    },
  },
  watch: {
    filter: {
      handler() {
        if (this.options.page !== 1) {
          this.options.page = 1
        } else {
          this.fetch()
        }
      },
      deep: true
    },
    options: {
      handler: 'fetch',
      deep: true
    },
    product: {
      handler() {
        if (this.options.page !== 1) {
          this.options.page = 1
        } else {
          this.fetch()
        }
      },
      deep: true
    }
  }
}
</script>

<style scoped>
.modern-card { border-radius: 12px !important; border: 1px solid rgba(0,0,0,0.05) !important; background-color: white !important; }
.shadow-sm { box-shadow: 0 4px 12px rgba(0,0,0,0.04) !important; }
.bg-lightgrey { background-color: #f8fbfc; }
.border-bottom { border-bottom: 1px solid rgba(0,0,0,0.05) !important; }

/* Table Styling */
.modern-table >>> thead th {
  background-color: #f8fbfc !important;
  height: 48px !important;
  text-transform: uppercase;
  font-size: 0.7rem !important;
  letter-spacing: 1px;
  font-weight: 900 !important;
  color: #607d8b !important;
}

.modern-table >>> td {
  height: 52px !important;
  border-bottom: 1px solid #f0f3f5 !important;
}

.uppercase { text-transform: uppercase; letter-spacing: 0.8px; }
.tracking-widest { letter-spacing: 1.8px; }
.tracking-tighter { letter-spacing: -0.5px; }
.lh-1-2 { line-height: 1.2 !important; }
.opacity-40 { opacity: 0.4; }
</style>